import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import { sanitiseOutgoing } from '../mail/html';

/*
    A deliberately small rich-text editor.

    It edits on white with dark text whatever theme the app is in, for the same
    reason Reader.tsx renders HTML mail on white: this is the recipient's page,
    not ours, and a message composed against a dark background arrives as
    dark-on-white or worse. What you type is what they get.

    It drives contenteditable with execCommand. That API is deprecated and has
    been for years, but it is implemented everywhere, it keeps the browser's own
    undo stack intact, and the alternative is either a 300 kB editor dependency
    or hand-written range surgery that gets Turkish input methods and RTL wrong.
    The output is sanitised on every change, so what execCommand produces never
    reaches a message unchecked.
*/

type Command =
    | { kind: 'exec'; id: string; arg?: string }
    | { kind: 'block'; tag: string }
    | { kind: 'link' }
    | { kind: 'image' };

const ICONS = {
    bold: 'M7 4.5h5.4a3.5 3.5 0 0 1 0 7H7zM7 11.5h6.2a4 4 0 0 1 0 8H7z',
    italic: 'M15.5 4.5h-5M13.5 19.5h-5M13.8 4.5l-3.6 15',
    underline: 'M6.5 4v6.5a5.5 5.5 0 0 0 11 0V4M5.5 20h13',
    strike: 'M5.5 12h13M8 8.2C8 6 9.8 4.5 12.3 4.5c2 0 3.4.8 4 2M16 15c.2 2.4-1.5 4.5-4.2 4.5-2.3 0-3.9-1-4.4-2.6',
    bullets: 'M9 6.5h11M9 12h11M9 17.5h11M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01',
    numbers: 'M9 6.5h11M9 12h11M9 17.5h11M4 4.5h1v4M3.6 11.2c.2-.6.7-.9 1.3-.9.7 0 1.1.4 1.1 1 0 1.1-2.4 1.6-2.4 2.7h2.5M3.6 16.4c.3-.4.8-.6 1.3-.6.7 0 1.2.4 1.2.9 0 .6-.5.9-1.2.9.8 0 1.3.3 1.3 1 0 .6-.6 1-1.3 1-.6 0-1.1-.2-1.4-.6',
    quote: 'M4 18V9.5A3.5 3.5 0 0 1 7.5 6M10 18V9.5A3.5 3.5 0 0 1 13.5 6M16 18V9.5A3.5 3.5 0 0 1 19.5 6',
    link: 'M10.5 13.5a4 4 0 0 0 5.7 0l2.6-2.6a4 4 0 0 0-5.7-5.7l-1.3 1.3M13.5 10.5a4 4 0 0 0-5.7 0l-2.6 2.6a4 4 0 0 0 5.7 5.7l1.3-1.3',
    image: 'M4 6.5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-11zM4 15l4.5-4 4 3.5 3-2.5L20 16M9 9.5h.01',
    clear: 'M7 6h13M11 6l-1.5 12M17 6l-1 8M4 20h8',
} as const;

const escapeText = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function Tool({ icon, title, onClick, active }: { icon: string; title: string; onClick: () => void; active?: boolean }) {
    return (
        <button
            type="button"
            title={title}
            // Keeps the selection in the editor: a focused button collapses it first.
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            style={{
                width: 26,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: active ? 'var(--accent-bg)' : 'transparent',
                border: '1px solid transparent',
                borderRadius: 6,
                color: active ? 'var(--accent-strong)' : 'var(--text-dim)',
            }}
        >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d={icon} />
            </svg>
        </button>
    );
}

export default function RichText({
    value,
    seed,
    onChange,
    minHeight,
    placeholder,
}: {
    /** Initial HTML. Read when `seed` changes, not on every render. */
    value: string;
    /** Bump to reload the editor from `value` — a new draft, a switched identity. */
    seed: string;
    onChange: (html: string) => void;
    minHeight?: number;
    /** Shown while the sheet is empty, so a blank white area reads as an
        invitation to type rather than as something that failed to load. */
    placeholder?: string;
}) {
    const { t } = useT();
    const ref = useRef<HTMLDivElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const [marks, setMarks] = useState<Record<string, boolean>>({});
    const [linking, setLinking] = useState<string | null>(null);
    const savedRange = useRef<Range | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.innerHTML = value;
        // <p> rather than <div> between paragraphs: mail clients space it correctly.
        try {
            document.execCommand('defaultParagraphSeparator', false, 'p');
        } catch {
            /* Firefox already does this */
        }
        // `value` is the seed's payload; re-reading it on every keystroke would
        // fight the caret.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seed]);

    const emit = useCallback(() => {
        if (ref.current) onChange(ref.current.innerHTML);
    }, [onChange]);

    const refreshMarks = useCallback(() => {
        const check = (id: string) => {
            try {
                return document.queryCommandState(id);
            } catch {
                return false;
            }
        };
        setMarks({
            bold: check('bold'),
            italic: check('italic'),
            underline: check('underline'),
            strikeThrough: check('strikeThrough'),
            insertUnorderedList: check('insertUnorderedList'),
            insertOrderedList: check('insertOrderedList'),
        });
    }, []);

    const run = useCallback(
        (command: Command) => {
            const el = ref.current;
            if (!el) return;
            el.focus();

            if (command.kind === 'exec') {
                document.execCommand(command.id, false, command.arg);
            } else if (command.kind === 'block') {
                // Toggling: pressing quote inside a quote should leave it.
                const inside = document.queryCommandValue('formatBlock').toLowerCase();
                document.execCommand('formatBlock', false, inside === command.tag ? 'p' : command.tag);
            } else if (command.kind === 'link') {
                // The URL bar takes focus, which collapses the selection — so the
                // range is kept here and restored when it's applied.
                const selection = window.getSelection();
                const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
                savedRange.current = range && el.contains(range.commonAncestorContainer) ? range.cloneRange() : null;
                setLinking(savedRange.current?.toString() ?? '');
                return;
            } else if (command.kind === 'image') {
                fileRef.current?.click();
                return;
            }

            emit();
            refreshMarks();
        },
        [emit, refreshMarks],
    );

    const applyLink = (raw: string) => {
        const url = /^[a-z]+:/i.test(raw) ? raw : `https://${raw}`;
        ref.current?.focus();

        const selection = window.getSelection();
        if (savedRange.current && selection) {
            selection.removeAllRanges();
            selection.addRange(savedRange.current);
        }

        // Nothing was selected, so there is no text to turn into a link — the URL
        // becomes its own label, which is what every client does.
        if (!savedRange.current || savedRange.current.collapsed) {
            const label = escapeText(raw);
            document.execCommand('insertHTML', false, `<a href="${escapeText(url)}">${label}</a>`);
        } else {
            document.execCommand('createLink', false, url);
        }

        savedRange.current = null;
        setLinking(null);
        emit();
    };

    /*
        Images go in as data: URIs and become real inline parts at send time
        (see extractInlineImages). Holding them in the HTML until then is what
        lets a draft round-trip through the server with its pictures intact.
    */
    const insertImages = async (files: FileList | null) => {
        for (const file of Array.from(files ?? [])) {
            if (!file.type.startsWith('image/')) continue;
            const data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result));
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(file);
            });
            ref.current?.focus();
            document.execCommand('insertHTML', false, `<img src="${data}" alt="${file.name}" style="max-width: 100%">`);
        }
        emit();
    };

    /*
        Paste is where foreign markup arrives. Word sends a kilobyte of mso-*
        styling per paragraph and a copied web page sends whatever it likes, so
        the fragment is sanitised before it is inserted rather than after.
    */
    const onPaste = (e: React.ClipboardEvent) => {
        const html = e.clipboardData.getData('text/html');
        const text = e.clipboardData.getData('text/plain');
        if (!html && !text) return;
        e.preventDefault();
        const fragment = html
            ? sanitiseOutgoing(html)
            : text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>');
        document.execCommand('insertHTML', false, fragment);
        emit();
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-btn)',
                overflow: 'hidden',
                /* The frame belongs to the app; only the sheet inside is white.
                   A full-bleed white slab under a dark toolbar reads as a
                   rendering fault rather than as a piece of paper. */
                background: 'var(--surface2)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    padding: '3px 5px',
                    background: 'var(--surface2)',
                    borderBottom: '1px solid var(--border)',
                }}
            >
                <Tool icon={ICONS.bold} title={t('rich.bold')} active={marks.bold} onClick={() => run({ kind: 'exec', id: 'bold' })} />
                <Tool icon={ICONS.italic} title={t('rich.italic')} active={marks.italic} onClick={() => run({ kind: 'exec', id: 'italic' })} />
                <Tool icon={ICONS.underline} title={t('rich.underline')} active={marks.underline} onClick={() => run({ kind: 'exec', id: 'underline' })} />
                <Tool icon={ICONS.strike} title={t('rich.strike')} active={marks.strikeThrough} onClick={() => run({ kind: 'exec', id: 'strikeThrough' })} />
                <span style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 5px' }} />
                <Tool icon={ICONS.bullets} title={t('rich.bullets')} active={marks.insertUnorderedList} onClick={() => run({ kind: 'exec', id: 'insertUnorderedList' })} />
                <Tool icon={ICONS.numbers} title={t('rich.numbers')} active={marks.insertOrderedList} onClick={() => run({ kind: 'exec', id: 'insertOrderedList' })} />
                <Tool icon={ICONS.quote} title={t('rich.quote')} onClick={() => run({ kind: 'block', tag: 'blockquote' })} />
                <span style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 5px' }} />
                <Tool icon={ICONS.link} title={t('rich.link')} onClick={() => run({ kind: 'link' })} />
                <Tool icon={ICONS.image} title={t('rich.image')} onClick={() => run({ kind: 'image' })} />
                <Tool icon={ICONS.clear} title={t('rich.clear')} onClick={() => run({ kind: 'exec', id: 'removeFormat' })} />
                <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => void insertImages(e.target.files)} style={{ display: 'none' }} />
            </div>

            {linking !== null ? (
                <div style={{ display: 'flex', gap: 6, padding: '6px 7px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                    <input
                        autoFocus
                        value={linking}
                        onChange={(e) => setLinking(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && linking.trim()) applyLink(linking.trim());
                            if (e.key === 'Escape') setLinking(null);
                        }}
                        placeholder="https://"
                        style={{
                            flex: 1,
                            height: 26,
                            padding: '0 9px',
                            background: 'var(--surface)',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--r-btn)',
                            outline: 'none',
                            fontSize: 'var(--fs-sm)',
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => linking.trim() && applyLink(linking.trim())}
                        style={{ height: 26, padding: '0 11px', background: 'var(--accent)', color: 'var(--on-accent)', border: 'none', borderRadius: 'var(--r-btn)', fontSize: 'var(--fs-sm)' }}
                    >
                        {t('rich.apply')}
                    </button>
                </div>
            ) : null}

            <div
                ref={ref}
                data-testid="rich-text"
                contentEditable
                suppressContentEditableWarning
                dir="auto"
                spellCheck
                className="rich-body"
                data-placeholder={placeholder ?? ''}
                onInput={emit}
                onBlur={emit}
                onPaste={onPaste}
                onKeyUp={refreshMarks}
                onMouseUp={refreshMarks}
                style={{
                    flex: 1,
                    minHeight: minHeight ?? 150,
                    overflowY: 'auto',
                    margin: 8,
                    padding: 12,
                    borderRadius: 8,
                    outline: 'none',
                    // Deliberately white whatever the theme is: this is the
                    // recipient's page, not ours. See the note at the top.
                    background: '#ffffff',
                    color: '#1f1f24',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.28)',
                    font: "13.5px/1.6 'Space Grotesk', system-ui, sans-serif",
                }}
            />
        </div>
    );
}
